import { apiClient } from './client';

export interface GameCreateRequest {
  character_id: number;
  game_type: string;
  options?: Record<string, any>;
}

export interface GameActionRequest {
  action: string;
  data?: Record<string, any>;
}

export interface GameRatingRequest {
  rating: number;
  feedback?: string;
  categories?: Record<string, number>;
}

export interface Game {
  id: string;
  game_type: string;
  status: string;
  character_id: number;
  entry_cost: number;
  coins_per_round: number;
  max_rounds: number;
  current_round: number;
  user_score: number;
  character_score: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  expires_at?: string;
}

export interface GameMove {
  id: string;
  round: number;
  player: string;
  type: string;
  prompt?: string;
  response?: string;
  choice?: string;
  points?: number;
  completed: boolean;
}

export interface GameState {
  game: Game;
  moves: GameMove[];
}

export interface AvailableGame {
  id: string;
  type: string;
  name: string;
  description: string;
  difficulty: number;
  category: string;
  entry_cost: number;
  coins_per_round: number;
  default_rounds: number;
  can_afford: boolean;
  plays_count: number;
  total_plays: number;
  average_rating: number;
  requires_subscription: boolean;
}

export interface GameListResponse {
  games: AvailableGame[];
}

export interface GameActionResponse {
  success: boolean;
  action: string;
  result: Record<string, any>;
  game_id: string;
}

export interface GameHistoryResponse {
  games: Game[];
}

export interface GameStatistics {
  total_games: number;
  games_by_status: Record<string, number>;
  games_by_type: Record<string, number>;
  total_coins_spent: number;
  average_rating_given: number;
  favorite_game_type?: string;
  completed_games: number;
  active_games: number;
}

export interface GameRating {
  id: string;
  rating: number;
  feedback?: string;
  fun_rating?: number;
  difficulty_rating?: number;
  character_performance?: number;
  would_play_again: boolean;
  created_at: string;
}

export const gameApi = {
  /**
   * Get available games for the user
   */
  async getAvailableGames(): Promise<GameListResponse> {
    const response = await apiClient.get('/games/');
    return response.data;
  },

  /**
   * Create a new game session
   */
  async createGame(request: GameCreateRequest): Promise<Game> {
    const response = await apiClient.post('/games/', request);
    return response.data;
  },

  /**
   * Start a game and handle billing
   */
  async startGame(gameId: string): Promise<Game> {
    const response = await apiClient.post(`/games/${gameId}/start`);
    return response.data;
  },

  /**
   * Get current state of a game
   */
  async getGameState(gameId: string): Promise<GameState> {
    const response = await apiClient.get(`/games/${gameId}`);
    return response.data;
  },

  /**
   * Process a game action
   */
  async processGameAction(gameId: string, request: GameActionRequest): Promise<GameActionResponse> {
    const response = await apiClient.post(`/games/${gameId}/action`, request);
    return response.data;
  },

  /**
   * Get user's game history
   */
  async getUserGameHistory(status?: string, limit = 20): Promise<GameHistoryResponse> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    
    const response = await apiClient.get(`/games/user/history?${params}`);
    return response.data;
  },

  /**
   * Rate a completed game
   */
  async rateGame(gameId: string, request: GameRatingRequest): Promise<GameRating> {
    const response = await apiClient.post(`/games/${gameId}/rate`, request);
    return response.data;
  },

  /**
   * Get user's game statistics
   */
  async getUserStatistics(): Promise<GameStatistics> {
    const response = await apiClient.get('/games/user/statistics');
    return response.data;
  },

  /**
   * Cancel an active game
   */
  async cancelGame(gameId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`/games/${gameId}`);
    return response.data;
  }
};

// WebSocket message helpers
export const createWSGameMessage = {
  joinGame: (gameId: string) => ({
    type: 'join_game',
    game_id: gameId
  }),

  leaveGame: (gameId: string) => ({
    type: 'leave_game',
    game_id: gameId
  }),

  gameAction: (gameId: string, action: string, data: Record<string, any> = {}) => ({
    type: 'game_action',
    game_id: gameId,
    action,
    data
  })
};

// Game type constants
export const GameTypes = {
  TRUTH_OR_DARE: 'truth_or_dare',
  TWENTY_QUESTIONS: 'twenty_questions',
  STRIP_POKER: 'strip_poker'
} as const;

// Game status constants
export const GameStatus = {
  WAITING: 'waiting',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
} as const;

// Move type constants
export const MoveTypes = {
  TRUTH: 'truth',
  DARE: 'dare',
  QUESTION: 'question',
  ANSWER: 'answer'
} as const;

// Game action constants
export const GameActions = {
  MAKE_CHOICE: 'make_choice',
  SUBMIT_RESPONSE: 'submit_response',
  GENERATE_CHARACTER_MOVE: 'generate_character_move',
  SUBMIT_USER_RESPONSE: 'submit_user_response'
} as const;